import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Paper,
  Tabs,
  Tab,
  Alert,
  Snackbar,
  CircularProgress,
  Switch,
  FormControlLabel,
  InputAdornment,
  Tooltip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import {
  AddOutlined as AddIcon,
  EditOutlined as EditIcon,
  DeleteOutlined as DeleteIcon,
  CheckCircleOutline as CheckCircleIcon,
  ScheduleOutlined as ScheduleIcon,
  RepeatOutlined as RepeatIcon,
  EventRepeatOutlined as EmptyIcon,
  EngineeringOutlined as ContractorIcon,
  PaymentsOutlined as CostIcon
} from '@mui/icons-material';
import api from '../api';
import CalendarView from './CalendarView';

const FREQUENCY_OPTIONS = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Every 3 months' },
  { value: 'semiannual', label: 'Every 6 months' },
  { value: 'yearly', label: 'Yearly' }
];
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const asArray = (data) => (Array.isArray(data) ? data : []);
const pad = (n) => String(n).padStart(2, '0');
const localToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/** 'YYYY-MM-DD' → local Date (avoids the UTC off-by-one of `new Date('YYYY-MM-DD')`). */
const parseDay = (str) => {
  const [y, m, d] = String(str).slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d);
};
const formatDay = (str) =>
  str ? parseDay(str).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
const daysFromToday = (str) => Math.round((parseDay(str) - parseDay(localToday())) / 86400000);

const ordinal = (n) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};

const frequencyText = (task) => {
  if (task.frequency === 'weekly') return `Every ${WEEKDAYS[task.dayOfWeek] || 'week'}`;
  const label = FREQUENCY_OPTIONS.find((f) => f.value === task.frequency)?.label || task.frequency;
  return task.dayOfMonth ? `${label} on the ${ordinal(task.dayOfMonth)}` : label;
};

const dueStatus = (task) => {
  if (!task.isActive) return { label: 'Paused', color: 'default' };
  if (!task.nextDueDate) return { label: 'Schedule ended', color: 'default' };
  const days = daysFromToday(task.nextDueDate);
  if (days < 0) return { label: `Overdue by ${-days} day${days === -1 ? '' : 's'}`, color: 'error' };
  if (days === 0) return { label: 'Due today', color: 'warning' };
  if (days <= 7) return { label: `Due in ${days} day${days === 1 ? '' : 's'}`, color: 'warning' };
  return { label: `Due ${formatDay(task.nextDueDate)}`, color: 'default' };
};

const priorityColor = (priority) => ({ high: 'error', medium: 'warning', low: 'success' }[priority] || 'default');

const emptyForm = () => ({
  title: '',
  description: '',
  category: '',
  propertyId: '',
  unitId: '',
  frequency: 'monthly',
  dayOfMonth: 1,
  dayOfWeek: 1,
  startDate: localToday(),
  endDate: '',
  contractorId: '',
  notifyContractor: false,
  estimatedCost: '',
  priority: 'medium',
  isActive: true
});

const ScheduledMaintenance = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [tasks, setTasks] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [properties, setProperties] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);

  const [completing, setCompleting] = useState(null);
  const [completeForm, setCompleteForm] = useState({ notes: '', actualCost: '' });

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    try {
      setLoading(true);
      setError(null);
      const [taskRes, templateRes, propRes, contractorRes] = await Promise.all([
        api.get('/api/scheduled-maintenance'),
        api.get('/api/scheduled-maintenance/templates').catch(() => ({ data: [] })),
        api.get('/api/properties').catch(() => ({ data: [] })),
        api.get('/api/contractors').catch(() => ({ data: [] }))
      ]);
      setTasks(asArray(taskRes.data));
      setTemplates(asArray(templateRes.data));
      setProperties(asArray(propRes.data));
      setContractors(asArray(contractorRes.data));
    } catch (err) {
      console.error('❌ Failed to load scheduled maintenance:', err);
      setError(err.response?.data?.error || 'Failed to load scheduled maintenance');
    } finally {
      setLoading(false);
    }
  };

  const refreshTasks = async () => {
    try {
      const res = await api.get('/api/scheduled-maintenance');
      setTasks(asArray(res.data));
    } catch (err) {
      console.error('❌ Failed to refresh scheduled maintenance:', err);
    }
  };

  const propertyById = (id) => properties.find((p) => p.id === id);
  const unitsFor = (propertyId) => asArray(propertyById(propertyId)?.units);
  const unitLabel = (unit) => unit?.unitNumber || unit?.name || unit?.id || '';
  const locationText = (task) => {
    const property = propertyById(task.propertyId);
    const propertyName = property?.name || property?.address || 'Unknown property';
    if (!task.unitId) return `${propertyName} · Whole property`;
    const unit = unitsFor(task.propertyId).find((u) => (u.id || u.unitId) === task.unitId);
    return `${propertyName} · ${unit ? unitLabel(unit) : 'Unit'}`;
  };
  const contractorById = (id) => contractors.find((c) => c.id === id);

  const openDialog = (task = null, template = null) => {
    setFormError(null);
    if (task) {
      setEditingTask(task);
      setFormData({
        ...emptyForm(),
        ...task,
        unitId: task.unitId || '',
        contractorId: task.contractorId || '',
        endDate: task.endDate || '',
        dayOfMonth: task.dayOfMonth ?? 1,
        dayOfWeek: task.dayOfWeek ?? 1,
        estimatedCost: task.estimatedCost ?? ''
      });
    } else {
      setEditingTask(null);
      setFormData({
        ...emptyForm(),
        ...(template && {
          title: template.name,
          description: template.description || '',
          category: template.category || '',
          frequency: template.frequency || 'monthly',
          estimatedCost: template.estimatedCost ?? '',
          priority: template.priority || 'medium'
        })
      });
    }
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (saving) return;
    setDialogOpen(false);
    setEditingTask(null);
  };

  const setField = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'propertyId') next.unitId = '';
      if (field === 'contractorId' && !value) next.notifyContractor = false;
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!formData.title.trim()) return setFormError('Give the task a name');
    if (!formData.propertyId) return setFormError('Choose a property');
    if (!formData.startDate) return setFormError('Choose a start date');

    const payload = {
      title: formData.title.trim(),
      description: formData.description,
      category: formData.category,
      propertyId: formData.propertyId,
      unitId: formData.unitId || null,
      frequency: formData.frequency,
      dayOfMonth: Number(formData.dayOfMonth),
      dayOfWeek: Number(formData.dayOfWeek),
      startDate: formData.startDate,
      endDate: formData.endDate || null,
      contractorId: formData.contractorId || null,
      notifyContractor: Boolean(formData.contractorId) && formData.notifyContractor,
      estimatedCost: formData.estimatedCost === '' ? 0 : Number(formData.estimatedCost),
      priority: formData.priority,
      isActive: formData.isActive
    };

    try {
      setSaving(true);
      setFormError(null);
      if (editingTask) {
        await api.put(`/api/scheduled-maintenance/${editingTask.id}`, payload);
        setToast('Scheduled task updated');
      } else {
        await api.post('/api/scheduled-maintenance', payload);
        setToast('Scheduled task added');
      }
      setDialogOpen(false);
      setEditingTask(null);
      await refreshTasks();
    } catch (err) {
      console.error('❌ Save scheduled task failed:', err);
      setFormError(err.response?.data?.error || 'Could not save the task');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (task) => {
    if (!window.confirm(`Delete "${task.title}"? Past completions for this task will be removed too.`)) return;
    try {
      await api.delete(`/api/scheduled-maintenance/${task.id}`);
      setToast('Scheduled task deleted');
      await refreshTasks();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not delete the task');
    }
  };

  const handleToggleActive = async (task) => {
    try {
      await api.put(`/api/scheduled-maintenance/${task.id}/active`, { isActive: !task.isActive });
      setToast(task.isActive ? 'Task paused' : 'Task resumed');
      await refreshTasks();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not update the task');
    }
  };

  const openComplete = (task) => {
    setCompleteForm({ notes: '', actualCost: task.estimatedCost ? String(task.estimatedCost) : '' });
    setCompleting(task);
  };

  const handleComplete = async () => {
    if (!completing) return;
    try {
      setSaving(true);
      const res = await api.post(`/api/scheduled-maintenance/${completing.id}/complete`, {
        notes: completeForm.notes,
        actualCost: completeForm.actualCost === '' ? null : Number(completeForm.actualCost)
      });
      const next = res.data?.nextDueDate;
      setToast(next ? `Marked done. Next due ${formatDay(next)}` : 'Marked done. No more dates on this schedule');
      setCompleting(null);
      await refreshTasks();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not mark the task done');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const selectedContractor = contractorById(formData.contractorId);
  const formUnits = unitsFor(formData.propertyId);

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 2,
          mb: 3
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
            Scheduled Maintenance
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Recurring jobs like filter changes and gutter cleaning. On the due date they're added to your
            Maintenance list automatically.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => openDialog()} fullWidth={isMobile}>
          Add scheduled task
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ width: '100%', mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} variant={isMobile ? 'fullWidth' : 'standard'}>
          <Tab label="Tasks" />
          <Tab label="Templates" />
          <Tab label="Calendar" />
        </Tabs>
      </Paper>

      {activeTab === 0 && tasks.length === 0 && (
        <Card variant="outlined">
          <CardContent sx={{ textAlign: 'center', py: { xs: 4, sm: 6 } }}>
            <EmptyIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No scheduled maintenance yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 460, mx: 'auto' }}>
              Set up the jobs that come around every month, season or year. You'll get an email reminder the
              week before, and the job lands in your Maintenance list when it's due.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button variant="contained" onClick={() => setActiveTab(1)}>
                Start from a template
              </Button>
              <Button variant="outlined" startIcon={<AddIcon />} onClick={() => openDialog()}>
                Add your own
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {activeTab === 0 && tasks.length > 0 && (
        <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }}>
          {tasks.map((task) => {
            const status = dueStatus(task);
            const contractor = contractorById(task.contractorId);
            return (
              <Grid item xs={12} md={6} lg={4} key={task.id}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', opacity: task.isActive ? 1 : 0.75 }}>
                  <CardContent sx={{ flexGrow: 1, p: { xs: 1.5, sm: 2 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                      <Typography variant="h6" component="h3" sx={{ fontSize: { xs: '1rem', sm: '1.15rem' } }}>
                        {task.title}
                      </Typography>
                      <Tooltip title={task.isActive ? 'Pause this task' : 'Resume this task'}>
                        <Switch
                          checked={Boolean(task.isActive)}
                          onChange={() => handleToggleActive(task)}
                          size="small"
                          slotProps={{
                            input: { role: 'switch', 'aria-label': `${task.isActive ? 'Pause' : 'Resume'} ${task.title}` }
                          }}
                        />
                      </Tooltip>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1.5 }}>
                      <Chip size="small" label={status.label} color={status.color} />
                      <Chip size="small" variant="outlined" label={task.priority} color={priorityColor(task.priority)} />
                    </Box>
                    {task.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        {task.description}
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                      <RepeatIcon fontSize="small" color="action" />
                      <Typography variant="body2">{frequencyText(task)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                      <ScheduleIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        Next: {formatDay(task.nextDueDate)}
                        {task.lastCompletedAt && ` · Last done ${new Date(task.lastCompletedAt).toLocaleDateString()}`}
                      </Typography>
                    </Box>
                    {contractor && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                        <ContractorIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          {contractor.name}
                          {task.notifyContractor ? ' · gets reminders' : ''}
                        </Typography>
                      </Box>
                    )}
                    {task.estimatedCost ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                        <CostIcon fontSize="small" color="action" />
                        <Typography variant="body2">Estimated cost ${task.estimatedCost}</Typography>
                      </Box>
                    ) : null}
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                      {locationText(task)}
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 1.5 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<CheckCircleIcon />}
                      onClick={() => openComplete(task)}
                      disabled={!task.isActive || !task.nextDueDate}
                    >
                      Mark done
                    </Button>
                    <Box>
                      <IconButton size="small" onClick={() => openDialog(task)} aria-label={`Edit ${task.title}`}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(task)} aria-label={`Delete ${task.title}`}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {activeTab === 1 && (
        <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }}>
          {templates.map((template) => (
            <Grid item xs={12} md={6} lg={4} key={template.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1, p: { xs: 1.5, sm: 2 } }}>
                  <Typography variant="h6" component="h3" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.15rem' } }}>
                    {template.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {template.description}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {FREQUENCY_OPTIONS.find((f) => f.value === template.frequency)?.label || template.frequency}
                    {template.estimatedCost ? ` · about\u00a0$${template.estimatedCost}` : ''}
                  </Typography>
                </CardContent>
                <CardActions sx={{ px: 2, pb: 1.5 }}>
                  <Button variant="outlined" size="small" onClick={() => openDialog(null, template)}>
                    Use template
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {activeTab === 2 && (
        <CalendarView scheduledTasks={tasks} properties={properties} onTaskClick={(task) => openDialog(task)} />
      )}

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle>{editingTask ? 'Edit scheduled task' : 'Add scheduled task'}</DialogTitle>
        <DialogContent dividers>
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Task name"
                value={formData.title}
                onChange={(e) => setField('title', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Details (optional)"
                value={formData.description}
                onChange={(e) => setField('description', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel id="sched-property-label">Property</InputLabel>
                <Select
                  labelId="sched-property-label"
                  label="Property"
                  value={formData.propertyId}
                  onChange={(e) => setField('propertyId', e.target.value)}
                >
                  {properties.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.name || p.address || p.id}
                    </MenuItem>
                  ))}
                </Select>
                {properties.length === 0 && <FormHelperText>Add a property first under Properties &amp; Units</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth disabled={!formData.propertyId}>
                <InputLabel id="sched-unit-label" shrink>
                  Unit
                </InputLabel>
                <Select
                  labelId="sched-unit-label"
                  label="Unit"
                  notched
                  displayEmpty
                  value={formData.unitId}
                  onChange={(e) => setField('unitId', e.target.value)}
                >
                  <MenuItem value="">Whole property</MenuItem>
                  {formUnits.map((u) => (
                    <MenuItem key={u.id || u.unitId} value={u.id || u.unitId}>
                      {unitLabel(u)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="sched-frequency-label">How often</InputLabel>
                <Select
                  labelId="sched-frequency-label"
                  label="How often"
                  value={formData.frequency}
                  onChange={(e) => setField('frequency', e.target.value)}
                >
                  {FREQUENCY_OPTIONS.map((f) => (
                    <MenuItem key={f.value} value={f.value}>
                      {f.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              {formData.frequency === 'weekly' ? (
                <FormControl fullWidth>
                  <InputLabel id="sched-dow-label">Day of the week</InputLabel>
                  <Select
                    labelId="sched-dow-label"
                    label="Day of the week"
                    value={formData.dayOfWeek}
                    onChange={(e) => setField('dayOfWeek', e.target.value)}
                  >
                    {WEEKDAYS.map((day, i) => (
                      <MenuItem key={day} value={i}>
                        {day}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <TextField
                  fullWidth
                  type="number"
                  label="Day of the month"
                  value={formData.dayOfMonth}
                  onChange={(e) => setField('dayOfMonth', e.target.value)}
                  inputProps={{ min: 1, max: 31 }}
                  helperText="Shorter months use their last day"
                />
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                type="date"
                label="Starting"
                value={formData.startDate}
                onChange={(e) => setField('startDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Ending (optional)"
                value={formData.endDate}
                onChange={(e) => setField('endDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="sched-contractor-label" shrink>
                  Contractor
                </InputLabel>
                <Select
                  labelId="sched-contractor-label"
                  label="Contractor"
                  notched
                  displayEmpty
                  value={formData.contractorId}
                  onChange={(e) => setField('contractorId', e.target.value)}
                >
                  <MenuItem value="">No contractor</MenuItem>
                  {contractors.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
                {contractors.length === 0 && <FormHelperText>Add contractors under Contractors to assign them here</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.notifyContractor}
                    onChange={(e) => setField('notifyContractor', e.target.checked)}
                    disabled={!selectedContractor?.email}
                  />
                }
                label="Email reminders to the contractor too"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Estimated cost"
                value={formData.estimatedCost}
                onChange={(e) => setField('estimatedCost', e.target.value)}
                inputProps={{ min: 0, step: '0.01' }}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="sched-priority-label">Priority</InputLabel>
                <Select
                  labelId="sched-priority-label"
                  label="Priority"
                  value={formData.priority}
                  onChange={(e) => setField('priority', e.target.value)}
                >
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={formData.isActive} onChange={(e) => setField('isActive', e.target.checked)} />}
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={closeDialog} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant="contained" disabled={saving}>
            {editingTask ? 'Save changes' : 'Add task'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(completing)} onClose={() => !saving && setCompleting(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Mark "{completing?.title}" done</DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            type="number"
            label="Actual cost (optional)"
            value={completeForm.actualCost}
            onChange={(e) => setCompleteForm((f) => ({ ...f, actualCost: e.target.value }))}
            inputProps={{ min: 0, step: '0.01' }}
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Notes (optional)"
            value={completeForm.notes}
            onChange={(e) => setCompleteForm((f) => ({ ...f, notes: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompleting(null)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleComplete} disabled={saving}>
            Mark done
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        message={toast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default ScheduledMaintenance;
